// Finishing a sale: every check, the invoice, its payment, and closing a table's order, in one
// commit. Quick bill and Counter's checkout both end here (docs/plans/stations.md).
import {
  buildInvoice,
  buildPayment,
  closeOrderOp,
  dispatchDelivery,
  formatQty,
  orderLines,
  setSalesOrderStatus,
  type BuiltInvoice,
  type PaymentMethod,
} from '@ims/core'
import type { CustomerRow, TenantSettingsRow } from '@ims/data'
import type { LocalRepository } from '@ims/local'
import { settlePaidAhead } from './allocate.ts'
import { loadBatchStock, planBatchLines } from './batches.ts'
import {
  belowMinPrice,
  missingSerials,
  stockShortfalls,
  type CartLine,
  type StockShortfall,
} from './cart.ts'
import { todayIso } from './format.ts'
import { numberingFor } from './numbering.ts'
import { monthInvoiceCount } from './offline.ts'
import { deliveryRow, isOpenOrder, orderItemRows, orderRow } from './orders.ts'
import { loadRecipes } from './recipes.ts'
import { salesOrderRow } from './sales-orders.ts'
import { loadSerials, saleProblem, saleSerialOps } from './serials.ts'
import type { SerialRow } from './rows.ts'

export interface SaleInput {
  repo: LocalRepository
  settings: TenantSettingsRow
  tenantId: string
  monthLimit: number | null
  isManager: boolean
  lines: readonly CartLine[]
  billDiscountPaisa: number
  customer: CustomerRow | null
  location: string | null
  paidPaisa: number
  creditPaisa: number
  method: PaymentMethod
  note: string
  orderId: string | null
  /** The order's lines when the screen loaded them: a different order now is refused. */
  orderBill: ReturnType<typeof orderLines> | null
  fromId: string | null
  batchProductIds: string[]
  /** The cashier already said "Sell anyway". */
  sellShort: boolean
  onSettleError: (error: unknown) => void
}

export type SaleResult =
  | { kind: 'refused'; message: string }
  | { kind: 'short'; short: StockShortfall[] }
  | {
      kind: 'done'
      built: BuiltInvoice
      numbering: Awaited<ReturnType<typeof numberingFor>>
      txId: string
    }

const refused = (message: string): SaleResult => ({ kind: 'refused', message })

export async function completeSale(input: SaleInput): Promise<SaleResult> {
  const { repo, settings, customer, location, isManager, method, orderId, orderBill, fromId } =
    input
  const lines = input.lines as CartLine[]
  if (input.creditPaisa > 0 && !customer)
    return refused('Choose a customer to sell on credit (amount received is less than the total).')
  const missing = missingSerials(lines)
  if (missing) return refused(missing)
  // The plan's bills for the month, as far as this device knows. The server
  // still accepts a bill made offline before the device could know.
  const monthLimit = input.monthLimit
  if (monthLimit !== null && (await monthInvoiceCount(repo)) >= monthLimit)
    return refused(
      `This month’s ${monthLimit} bills on your plan are used. Upgrade the plan (Settings → Subscription) to keep billing.`,
    )
  // More than the location has? Staff are stopped when the business doesn't allow
  // it (managers may go ahead); otherwise the cashier confirms. Checked on this
  // device only: the server never refuses a sale that happened at the counter.
  if (!input.sellShort) {
    const levels = await repo.stock.levels(location ? { locationId: location } : {})
    const short = stockShortfalls(
      lines,
      new Map(levels.map((l) => [l.product_id, l.qty_milli] as const)),
    )
    if (short.length > 0) {
      if (!settings.allow_negative_stock && !isManager)
        return refused(
          `Not enough stock: ${short.map((x) => `${x.description} (${formatQty(Math.max(0, x.haveMilli))} left)`).join(', ')}. Receive the stock first, or ask a manager.`,
        )
      return { kind: 'short', short }
    }
  }
  const today = todayIso()
  // Each unit is sold once: check the register as it is right now.
  const units = new Map<string, SerialRow | null>()
  const warranty = new Map<string, number | null>()
  for (const l of lines) {
    if (!l.trackSerials || !l.productId || !l.serials?.length) continue
    const found = await loadSerials(repo, input.tenantId, l.productId, l.serials)
    for (const [serial, row] of found) {
      const problem = saleProblem(serial, row)
      if (problem) return refused(problem)
      units.set(serial, row)
    }
    warranty.set(l.productId, (await repo.get('products', l.productId))?.warranty_months ?? null)
  }
  // Batches are chosen from the stock as it is right now.
  const plan = planBatchLines(
    lines,
    await loadBatchStock(repo, input.batchProductIds, location),
    today,
    settings.expired_sale,
  )
  if (plan.blocked) return refused(`${plan.blocked}. Choose another batch or remove the item.`)
  // The order as it is right now: another device may have added to it or closed it.
  const order = orderId ? await repo.orders.detail(orderId) : null
  if (orderId) {
    if (!order || !isOpenOrder(order.order)) return refused('This order is already closed.')
    if (JSON.stringify(orderLines(orderItemRows(order.items))) !== JSON.stringify(orderBill))
      return refused('The order changed. Check the bill and complete it again.')
  }
  // The delivery as it is now: it leaves with this bill, so it must still be waiting.
  const leaving = orderId ? await repo.deliveries.forOrder(orderId) : null
  if (leaving && leaving.status !== 'pending') return refused('This delivery has already left.')
  // The quotation or order as it is right now: another device may have closed it.
  const from = fromId ? await repo.salesOrders.detail(fromId) : null
  if (fromId) {
    const open = from && ['sent', 'accepted', 'confirmed'].includes(from.order.status)
    if (!from || !open)
      return refused('This document is a draft or already closed, so it cannot be billed.')
  }
  const numbering = await numberingFor(repo, settings, 'sales_invoice', today)
  const recipes = await loadRecipes(
    repo,
    plan.lines.map((l) => l.productId),
  )
  const hsCodes = new Map<string, string | null>()
  for (const id of new Set(plan.lines.flatMap((l) => l.productId ?? [])))
    hsCodes.set(id, (await repo.get('products', id))?.hs_code ?? null)
  const ctx = { tenantId: input.tenantId, now: new Date().toISOString() }
  const built = buildInvoice(ctx, {
    status: 'finalized',
    invoiceDate: today,
    locationId: location,
    currency: settings.currency,
    customer: customer
      ? {
          id: customer.id,
          name: customer.name,
          pan: customer.pan_vat_number,
          // A delivery's bill shows where it went.
          address: leaving?.address ?? customer.address,
        }
      : null,
    // A dish takes its ingredients out of stock, by its recipe as it is now.
    lines: plan.lines.map((l) => ({
      ...l,
      components: l.productId ? (recipes.get(l.productId) ?? null) : null,
      hsCode: l.productId ? (hsCodes.get(l.productId) ?? null) : null,
    })),
    documentDiscountPaisa: input.billDiscountPaisa,
    roundOff: 'nearest_rupee',
    notes: input.note.trim() || null,
    numbering,
    salesOrderId: from?.order.id ?? null,
  })
  const allocated = Math.min(input.paidPaisa, built.invoice.total_paisa)
  const ops = [
    ...built.ops,
    ...saleSerialOps(ctx, built.lines, units, today, (id) => warranty.get(id) ?? null),
  ]
  if (allocated > 0) {
    ops.push(
      ...buildPayment(ctx, {
        customerId: customer?.id ?? null,
        amountPaisa: allocated,
        method,
        paymentDate: today,
        allocations: [{ invoiceId: built.invoice.id, amountPaisa: allocated }],
      }).ops,
    )
  }
  // Staff can't go below a minimum price when the business blocks it (the
  // server checks the same, including each line's share of a bill discount).
  if (settings.min_price_rule === 'block' && !isManager) {
    const mins = new Map(lines.map((l) => [l.productId, l.minPricePaisa]))
    const low = built.lines.find(
      (l) =>
        belowMinPrice({
          qtyMilli: l.qty_milli,
          unitPricePaisa: l.unit_price_paisa,
          discountPaisa: l.discount_paisa,
          minPricePaisa: mins.get(l.product_id) ?? null,
        }) !== null,
    )
    if (low)
      return refused(`${low.description} is below its minimum price. Ask a manager to approve it.`)
  }
  // The order closes in the same tx as its invoice; a delivery leaves with it.
  if (order) ops.push(closeOrderOp(ctx, orderRow(order.order), built.invoice.id))
  if (leaving) ops.push(...dispatchDelivery(ctx, deliveryRow(leaving)).ops)
  // Billing a quotation is the customer saying yes.
  if (from?.order.kind === 'quotation' && from.order.status === 'sent')
    ops.push(...setSalesOrderStatus(ctx, salesOrderRow(from.order), 'accepted').ops)
  const { txId } = await repo.commit(ops)
  // Money the customer paid ahead comes off this bill: its own commit after the sale, so
  // a refusal (another device used the same money) never costs the sale.
  if (customer) void settlePaidAhead(repo, ctx, customer.id).catch(input.onSettleError)
  return { kind: 'done', built, numbering, txId }
}
